import * as React from 'react';
import { 
  Box,
  CircularProgress,
  DialogContent,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Autocomplete,
  TextField
} from '@mui/material';

import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField } from '../../components/common/Modal';

import { 
  upsertDimensionDefault, 
  getDimensionDefaultsByCube 
} from '../../slices/dimensionDefaults';
import cubeService from '../../services/cube';

export default function DimensionDefaultModal(props) {
  const {open, mode, data, handleClose, onSuccess} = props;

  const [loading, setLoading] = React.useState(false);
  const [cubes, setCubes] = React.useState([]);
  const [dimensions, setDimensions] = React.useState([]);
  
  const [selectedCube, setSelectedCube] = React.useState(null);
  const [selectedDimension, setSelectedDimension] = React.useState(null);
  const [defaultMember, setDefaultMember] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [id, setId] = React.useState('');

  const dispatch = useDispatch();

  // Load cubes on mount
  React.useEffect(() => {
    cubeService.getCubes(1, 100)
      .then(res => {
        setCubes(res.items || []);
      })
      .catch(err => console.error('Error loading cubes:', err));
  }, []);

  // Load dimensions when cube is selected
  React.useEffect(() => {
    if (selectedCube) {
      cubeService.getCube(selectedCube.id)
        .then(res => {
          setDimensions(res.dimensions || []);
        })
        .catch(err => console.error('Error loading dimensions:', err));
    } else {
      setDimensions([]);
    }
  }, [selectedCube]);

  React.useEffect(() => {
    if(mode === "Create") {
      setSelectedCube(null);
      setSelectedDimension(null);
      setDefaultMember("");
      setDescription("");
    } else if (data) {
      setId(data.id);
      setDefaultMember(data.default_member || "");
      setDescription(data.description || "");
      
      // Find and set the cube
      const cube = cubes.find(c => c.id === data.cube_id);
      if (cube) {
        setSelectedCube(cube);
      }
      
      // Set dimension (will be available after cube is loaded)
      if (data.oracle_epm_metadata_id) {
        setTimeout(() => {
          const dim = dimensions.find(d => d.oracle_epm_metadata_id === data.oracle_epm_metadata_id);
          if (dim) {
            setSelectedDimension(dim);
          }
        }, 500);
      }
    }
  }, [mode, data, cubes]);

  const handleSave = (e) => {
    if (!selectedCube || !selectedDimension) {
      alert('Please select both cube and dimension');
      return;
    }

    setLoading(true);
    
    const dimensionDefaultData = {
      cube_id: selectedCube.id,
      oracle_epm_metadata_id: selectedDimension.oracle_epm_metadata_id,
      default_member: defaultMember,
      description: description
    };

    dispatch(upsertDimensionDefault(dimensionDefaultData))
      .then(res => {
        setLoading(false);
        if (onSuccess) {
          onSuccess();
        }
        // Refresh the list for this cube
        if (selectedCube) {
          dispatch(getDimensionDefaultsByCube(selectedCube.id));
        }
        handleClose();
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
        alert('Error saving dimension default: ' + (err.message || 'Unknown error'));
      });
  };

  return (
    <>
      <CDialog
        open={open}
        onClose={handleClose}
        maxWidth={"md"}
        fullWidth
      >
        <CDialogTitle>
          {mode === 'Create' ? 'New Dimension Default' : 'Edit Dimension Default'}
        </CDialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 6,
            top: 6,
            color: (theme) => theme.palette.grey[800],
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Cube Selection */}
            <Autocomplete
              value={selectedCube}
              onChange={(event, newValue) => {
                setSelectedCube(newValue);
                setSelectedDimension(null); // Reset dimension when cube changes
              }}
              options={cubes}
              getOptionLabel={(option) => option.cube_name || ''}
              disabled={mode === 'Edit'} // Don't allow changing cube in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cube"
                  size="small"
                  required
                />
              )}
            />

            {/* Dimension Selection */}
            <Autocomplete
              value={selectedDimension}
              onChange={(event, newValue) => {
                setSelectedDimension(newValue);
              }}
              options={dimensions}
              getOptionLabel={(option) => option.dimension || ''}
              disabled={!selectedCube || mode === 'Edit'} // Don't allow changing dimension in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dimension"
                  size="small"
                  required
                  helperText={!selectedCube ? "Select a cube first" : ""}
                />
              )}
            />

            {/* Default Member */}
            <CTextField
              id="default-member" 
              label="Default Member"
              size="small"
              variant="outlined"
              value={defaultMember}
              onChange={(e) => setDefaultMember(e.target.value)}
              placeholder="e.g., Sales, Total Organization, FY2024"
              fullWidth
              required
            />

            {/* Description */}
            <CTextField
              id="description" 
              label="Description"
              size="small"
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Explain when this default should be used"
              fullWidth
              multiline
              rows={3}
            />

            {selectedCube && selectedDimension && (
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: 1,
                fontSize: '0.875rem'
              }}>
                <strong>Example:</strong> When a user queries "{selectedCube.cube_name}" without 
                specifying the {selectedDimension.dimension} dimension, the system will use 
                "{defaultMember || '(default value)'}" as the default.
              </Box>
            )}
          </Box>
        </DialogContent>
        <CDialogActions>
          <CButton variant='outlined' onClick={handleClose}>
            Cancel
          </CButton>
          <CButton variant='contained' onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </CButton>
        </CDialogActions>
      </CDialog>
    </>
  );
}

